import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Stack, MessageBar, MessageBarType } from "@fluentui/react";
import { Selection } from "@fluentui/react/lib/DetailsList";
import { mergeStyleSets } from "@fluentui/react/lib/Styling";
import { useTheme } from "../../../../theme/OAS365Theme";
import { useCheckin } from "../../../../redux/hooks/useCheckin";
import { useDocumentSearch } from "../../../../redux/hooks/useDocumentSearch";
import { useMetadata } from "../../../../redux/hooks/useMetadata";
import { useActivePage } from "../../../../redux/hooks/useActivePage";
import { useCheckout } from "../../../../redux/hooks/useCheckout";
import { useTools } from "../../../../redux/hooks/useTools";
import { useWorkingDocument } from "../../../../redux/hooks/useWorkingDocument";
import CustomerConfig from "../../../../azure/CustomerConfig";
import {
  CHECKED_OUT_TO_ME,
  DOCUMENT_LOAD_STATUS,
  INVALID_DOCUMENT,
  SEARCH_VALUE_ENCODING,
  REFRESH_TOKEN_ACTION,
} from "../../../../redux/constants/metaData";
import {
  OVERWRITE_DOCUMENT_CONFIRMATION_TITLE,
  OVERWRITE_DOCUMENT_CONFIRMATION_MSG,
} from "../../../../redux/constants/messageTypes";
import { getDocumentTableItems } from "./documentTableItems";
import { isValidPlayload } from "../../../../functions/reduxMethod";
import { isWorkingDocumentDownloaded } from "../../../../functions/helperMethod";
import SpinnerModal from "../../../../components/Spinner";
import Loader from "../../../../components/LoadSpinner";
import { useDocumentTableColumns } from "./AllDocumentsTableColumns";
import DocumentsTable from "./DocumentsTable";
import DocumentsPagination from "./DocumentsPagination";
import AllDocumentsSearchBar from "./AllDocumentsSearchBar";
import ConfirmModal from "../../../../components/popupModals/ConfirmModal";
import ReusableMessageDialog from "../../../../components/popupModals/ReusableMessageDialog";
import { ReusableDialog } from "../../../../components/popupModals/ReusableDialog";
import DocumentDetailsLoadingContent from "../../../../components/moreDetails/DocumentDetailsLoadingContent";
import { useTeamsRequestContext } from "../../../../redux/hooks/useTeamsRequestContext";
import { useLogin } from "../../../../redux/hooks/useLogin";
import { useOpenInIFS } from "../../../../redux/hooks/useOpenInIFS";
import { useDocumentDetails } from "../../../../redux/hooks/useDocumentDetails";
import { usePreviewInTeams } from "../../../../redux/hooks/usePreviewInTeams";
import { useUploadToOneDrive } from "../../../../redux/hooks/useUploadToOneDrive";
import { APP_ROUTES, NAV_LOCATION_STATE } from "../../../../routes/routeConfig";
import { useAppDispatch } from "../../../../redux/hooks";
import { ON_ATTACHED_DOC_INFO, ON_SUCCESS_MESSAGE_INVOKE } from "../../../../redux/constants/actionTypes";
import { SUCCESS_TYPE } from "../../../../redux/constants/messageTypes";

function encodeSearchValue(value) {
  if (!value) return SEARCH_VALUE_ENCODING.SEARCH_VALUE_DEFAULT;
  return encodeURIComponent(
    value.replace(new RegExp(SEARCH_VALUE_ENCODING.HASH_SYMBOL, "g"), SEARCH_VALUE_ENCODING.HASH_ENCODED)
  );
}

const RECENT_CHECKIN_HIGHLIGHT_MS = 10_000;

function generateWorkingDocumentFileName(doc) {
  const title = doc?.Title || doc?.title || "";
  const docClass = doc?.class || doc?.docClass || "";
  const docNo = doc?.DocNo || doc?.docNo || "";
  const docSheet = doc?.sheetNo || doc?.docSheet || "";
  const docRev = doc?.DocRev || doc?.docRev || "";
  const ext = doc?.FileExtension || doc?.fileExtension || "";
  return `${title} (${docClass} - ${docNo} - ${docSheet} - ${docRev}) - 1.${ext}`;
}

const DocumentsContent = ({ isPanelActive = true }) => {
  const dispatch = useAppDispatch();
  const {
    searchDocuments,
    goToPage,
    loading: searchLoading,
    data,
    documents: documentsForTable,
    selectedPageIndex,
    pageSize,
    setPageSize: setSearchPageSize,
    isError: searchError,
    message: searchErrorMessage,
    clearSearch,
    refreshTrigger,
    lastSearchParams,
    pendingRefreshOnNextMount,
    markAllDocumentsStaleAfterCheckout,
    clearPendingAllDocumentsRefresh,
  } = useDocumentSearch();
  const metadata = useMetadata();
  const { tenantId: requestTenantId, groupId: requestGroupId, channelId: requestChannelId } = useTeamsRequestContext();
  const { teamsContext } = useLogin();
  // General/common app has no live channel scope.
  // Use this to decide whether we should send empty groupId/channelId to the backend.
  const isGeneralAppContext = !teamsContext?.channel?.id;
  // In channel mode, prefer the live Teams context values (avoid stale local fallbacks).
  const effectiveChannelId = isGeneralAppContext
    ? ""
    : teamsContext?.channel?.id ?? requestChannelId ?? "";
  // In general-app mode (no channel), Working Documents queries MainMenu with teamId="",
  // so keep upload/refresh scoped the same to ensure newly uploaded checked-out docs appear.
  const effectiveGroupId = isGeneralAppContext
    ? ""
    : teamsContext?.channel?.membershipType === "Private"
      ? teamsContext?.channel?.ownerGroupId ?? requestGroupId ?? ""
      : teamsContext?.team?.groupId ?? requestGroupId ?? "";
  const effectiveFileRoot = effectiveChannelId ? "/ChannelDoc" : "/MainMenu";
  const checkout = useCheckout();
  const { onWorkingDocumentListStatusChange } = useTools();
  const { openInIFS, openIfsLoading, openIfsDialogOpen, openIfsDialogType, openIfsDialogTitle, openIfsDialogMessage, openIfsDialogDetails, closeOpenIfsDialog } = useOpenInIFS();
  const { workingDocumentList, onWorkingDocumentLoad } = useWorkingDocument();
  const { attachedDocInfo, docClassFormatList } = useCheckin();
  const details = useDocumentDetails();
  const { state: previewState, previewInTeams } = usePreviewInTeams();
  const { state: uploadState, uploadDocumentToOneDrive, closeDownloadSuccessDialog } = useUploadToOneDrive();

  const activePage = useActivePage();
  const tokenRefreshPreActionInvoke = metadata.tokenRefreshPreActionInvoke;
  const { checkoutDocument: checkoutDocumentForRefresh, checkoutDocumentStatus, performCheckout, getCheckoutButtonText, checkoutFlow } = checkout;
  const navigate = useNavigate();
  const location = useLocation();
  const skipInitialSearchRef = useRef(
    Boolean(location.state?.[NAV_LOCATION_STATE.skipWorkingDocumentsRefresh]),
  );
  const hasNavigatedForCheckoutSuccess = useRef(false);
  const hasRefreshedForUndoCheckoutSuccess = useRef(false);
  const hasNavigatedForDownloadSuccess = useRef(false);
  const hasShownDownloadToast = useRef(false);
  const prevCheckoutLoadingRef = useRef(Boolean(checkoutFlow?.loading));
  const prevDownloadSuccessDialogOpenRef = useRef(
    Boolean(uploadState?.downloadSuccessDialogOpen)
  );
  const advanceSearchReleaseLevel = metadata.advanceSearchReleaseLevel;
  const advanceSearchStartDate = metadata.advanceSearchStartDate;
  const advanceSearchEndDate = metadata.advanceSearchEndDate;
  const advanceSearchAnyDate = metadata.advanceSearchAnyDate;
  const advanceSearchOnlyUserInvolved = metadata.advanceSearchOnlyUserInvolved;
  const checkedOutToMe = metadata.checkedOutToMe;

  const [searchValue, setSearchValue] = useState("");
  const [lastExecutedSearchValue, setLastExecutedSearchValue] = useState("");
  const [selectedFileType, setSelectedFileType] = useState("");
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [isUserInvolvedOnly, setIsUserInvolvedOnly] = useState(true);
  const [showDownloadConfirm, setShowDownloadConfirm] = useState(false);
  const [itemToDownload, setItemToDownload] = useState(null);
  const selection = useMemo(() => new Selection({ getKey: (item) => item.key }), []);

  const theme = useTheme();
  const classNames = mergeStyleSets({
    wrapper: { position: "relative", maxHeight: "inherit", overflowY: "hidden" },
  });
  const lastEffectiveScopeKeyRef = useRef("");
  const recentCheckinHighlightTimerRef = useRef(null);
  const [recentCheckinDocForHighlight, setRecentCheckinDocForHighlight] = useState(null);

  const refreshWorkingDocumentsList = useCallback(() => {
    if (!requestTenantId) return;
    onWorkingDocumentLoad(
      effectiveGroupId,
      effectiveChannelId,
      requestTenantId,
      effectiveFileRoot,
      20,
    );
    onWorkingDocumentListStatusChange(DOCUMENT_LOAD_STATUS.onRefresh);
  }, [
    requestTenantId,
    effectiveGroupId,
    effectiveChannelId,
    effectiveFileRoot,
    onWorkingDocumentLoad,
    onWorkingDocumentListStatusChange,
  ]);

  useEffect(() => {
    if (attachedDocInfo === INVALID_DOCUMENT) {
      setRecentCheckinDocForHighlight(null);
      if (recentCheckinHighlightTimerRef.current) {
        clearTimeout(recentCheckinHighlightTimerRef.current);
        recentCheckinHighlightTimerRef.current = null;
      }
      return;
    }
    if (!isValidPlayload(attachedDocInfo)) {
      return;
    }
    setRecentCheckinDocForHighlight(attachedDocInfo);
    if (recentCheckinHighlightTimerRef.current) {
      clearTimeout(recentCheckinHighlightTimerRef.current);
    }
    recentCheckinHighlightTimerRef.current = setTimeout(() => {
      recentCheckinHighlightTimerRef.current = null;
      setRecentCheckinDocForHighlight(null);
      dispatch({ type: ON_ATTACHED_DOC_INFO, payload: null });
    }, RECENT_CHECKIN_HIGHLIGHT_MS);
  }, [attachedDocInfo, dispatch]);

  useEffect(() => {
    return () => {
      if (recentCheckinHighlightTimerRef.current) {
        clearTimeout(recentCheckinHighlightTimerRef.current);
        recentCheckinHighlightTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    setSearchPageSize(new CustomerConfig().getPageLimit() ?? 20);
  }, [setSearchPageSize]);

  useEffect(() => {
    if (!uploadState?.downloadSuccessDialogOpen) return;
    const t = setTimeout(closeDownloadSuccessDialog, 3000);
    return () => clearTimeout(t);
  }, [uploadState?.downloadSuccessDialogOpen, closeDownloadSuccessDialog]);

  useEffect(() => {
    const isDownloadSuccess =
      uploadState?.downloadSuccessDialogOpen &&
      !uploadState?.loading &&
      !uploadState?.error;
    if (isDownloadSuccess && !hasShownDownloadToast.current) {
      hasShownDownloadToast.current = true;
      const documentTitle = itemToDownload?.Title || itemToDownload?.title || "Document";
      dispatch({
        type: ON_SUCCESS_MESSAGE_INVOKE,
        payload: {
          type: SUCCESS_TYPE,
          title: "Download completed",
            message: `${documentTitle} downloaded successfully!`,
        },
      });
    }
    if (!uploadState?.downloadSuccessDialogOpen) {
      hasShownDownloadToast.current = false;
    }
  }, [uploadState?.downloadSuccessDialogOpen, uploadState?.loading, uploadState?.error, dispatch, itemToDownload]);

  // When document download (upload to OneDrive) succeeds, navigate to Working Documents
  useEffect(() => {
    const isDialogOpen = Boolean(uploadState?.downloadSuccessDialogOpen);
    const openedNow = isDialogOpen && !prevDownloadSuccessDialogOpenRef.current;
    const isDownloadSuccess =
      openedNow &&
      !uploadState?.loading &&
      !hasNavigatedForDownloadSuccess.current;
    if (isDownloadSuccess) {
      hasNavigatedForDownloadSuccess.current = true;
      markAllDocumentsStaleAfterCheckout();
      navigate(APP_ROUTES.workingDocuments, {
        state: { [NAV_LOCATION_STATE.forceRefreshWorkingDocuments]: true },
      });
    }
    if (!isDialogOpen) {
      hasNavigatedForDownloadSuccess.current = false;
    }
    prevDownloadSuccessDialogOpenRef.current = isDialogOpen;
  }, [
    uploadState?.downloadSuccessDialogOpen,
    uploadState?.loading,
    navigate,
    markAllDocumentsStaleAfterCheckout,
  ]);

  const buildSearchParams = useCallback(
    (pageIndex, overrides = {}) => {
      const effectiveSearchValue =
        overrides.searchValue !== undefined ? overrides.searchValue : searchValue;
      const search = encodeSearchValue(effectiveSearchValue);
      return {
        searchValue: "'" + search + "'",
        searchBy: "''",
        pageSize,
        searchType: "BASIC_SEARCH",
        advanceSearchReleaseLevel,
        advanceSearchStartDate,
        advanceSearchEndDate,
        advanceSearchOnlyUserInvolved: isUserInvolvedOnly,
        advanceSearchAnyDate,
        advanceSearchDocClass: "''",
        advanceSearchFormat: "''",
        externalSearch: false,
        fileStatus: activePage === CHECKED_OUT_TO_ME ? "'Checked Out'" : "''",
        tenantId: requestTenantId,
        // In general-app mode (no channel scope), we must avoid object-connection filtering.
        // Send empty groupId/channelId so the backend treats it as "main menu".
        groupId: effectiveGroupId,
        channelId: effectiveChannelId,
        lastCheckOutMe: checkedOutToMe,
        partiallyCompleted: false,
        docType: "'" + (selectedFileType ?? "") + "'",
        pageIndex: pageIndex ?? selectedPageIndex,
        isIfsFileAccessible: true,
      };
    },
    [
      searchValue,
      pageSize,
      advanceSearchReleaseLevel,
      advanceSearchStartDate,
      advanceSearchEndDate,
      advanceSearchOnlyUserInvolved,
      advanceSearchAnyDate,
      activePage,
      requestTenantId,
      effectiveGroupId,
      effectiveChannelId,
      checkedOutToMe,
      selectedFileType,
      selectedPageIndex,
      isUserInvolvedOnly,
    ]
  );

  const runSearch = useCallback(
    (pageIndex, overrides = {}) => {
      if (!requestTenantId) return;
      const params = buildSearchParams(pageIndex ?? selectedPageIndex, overrides);
      searchDocuments(params);
    },
    [requestTenantId, buildSearchParams, searchDocuments, selectedPageIndex]
  );

  const handleClearAndSearchAll = useCallback(() => {
    setSearchValue("");
    setLastExecutedSearchValue("");
    runSearch(0, { searchValue: "" });
  }, [runSearch]);

  const handleSearchExecute = useCallback(() => {
    const term = searchValue ?? "";
    setLastExecutedSearchValue(term);
    runSearch(0, { searchValue: term });
  }, [searchValue, runSearch]);

  const runSearchRef = useRef(runSearch);
  runSearchRef.current = runSearch;
  const lastHandledRefreshTriggerRef = useRef(0);
  const isFirstRefreshTriggerSyncRef = useRef(true);
  const handledPendingRefreshRef = useRef(false);

  // Initial search: run when tenant/scope changes or no cached results (e.g. first visit).
  // Skip refetch when returning from check-in cancel — Redux still holds the last search.
  // Intentionally omit runSearch from deps so this effect only runs on tenant/scope change, not when filter changes
  // (otherwise filter change would trigger both this effect and the filter effect → duplicate API calls).
  useEffect(() => {
    if (!requestTenantId) return;
    if (skipInitialSearchRef.current) {
      skipInitialSearchRef.current = false;
      return;
    }
    const effectiveScopeKey = `${effectiveGroupId ?? ""}|${effectiveChannelId ?? ""}`;
    const scopeChanged = lastEffectiveScopeKeyRef.current !== effectiveScopeKey;
    const hasCachedSearch =
      lastSearchParams != null && data != null && !searchLoading;
    if (scopeChanged) {
      lastEffectiveScopeKeyRef.current = effectiveScopeKey;
      runSearchRef.current(0);
      return;
    }
    if (!hasCachedSearch) {
      lastEffectiveScopeKeyRef.current = effectiveScopeKey;
      runSearchRef.current(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- scope-aware re-run only
  }, [requestTenantId, effectiveGroupId, effectiveChannelId]);

  // Run search when file type or accessible-only filter changes (not when searchValue/runSearch identity changes)
  useEffect(() => {
    if (!requestTenantId || lastSearchParams == null) return;
    runSearchRef.current(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only file type and accessible-only
  }, [selectedFileType, isUserInvolvedOnly, requestTenantId]);

  // Refresh All Documents when requested (e.g. after check-in existing from Working Documents).
  // Always refetch page 1 so the list matches the server after check-in. Do not depend on
  // selectedPageIndex — when the search fulfills, page index updates and would re-run this effect
  // and duplicate API calls.
  // Refetch only when refreshTrigger increments, not when the accordion reopens (isPanelActive).
  useEffect(() => {
    if (!requestTenantId || !isPanelActive) return;
    const trigger = refreshTrigger ?? 0;
    if (isFirstRefreshTriggerSyncRef.current) {
      isFirstRefreshTriggerSyncRef.current = false;
      lastHandledRefreshTriggerRef.current = trigger;
      return;
    }
    if (trigger === 0 || trigger === lastHandledRefreshTriggerRef.current) return;
    lastHandledRefreshTriggerRef.current = trigger;
    runSearchRef.current(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refreshTrigger increments only
  }, [refreshTrigger, requestTenantId, isPanelActive]);

  // After check-out, refetch once when the panel opens while the pending flag is set.
  useEffect(() => {
    if (!pendingRefreshOnNextMount) {
      handledPendingRefreshRef.current = false;
      return;
    }
    if (!requestTenantId || !isPanelActive) return;
    if (handledPendingRefreshRef.current) return;
    handledPendingRefreshRef.current = true;
    clearPendingAllDocumentsRefresh();
    runSearchRef.current(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pending flag transitions only
  }, [requestTenantId, pendingRefreshOnNextMount, isPanelActive]);

  const tableItems = useMemo(() => {
    const baseDocuments = documentsForTable ?? [];

    const normalizedSearch = (lastExecutedSearchValue ?? "").trim().toLowerCase();
    const shouldApplyLocalSearchFilter =
      isGeneralAppContext && normalizedSearch.length > 0;

    const visibleDocuments = shouldApplyLocalSearchFilter
      ? baseDocuments.filter((doc) => {
          const searchableFields = [
            doc?.Title,
            doc?.docNo,
            doc?.docRev,
            doc?.docSheet,
            doc?.docClass,
            doc?.DocRespName,
            doc?.DocClassName,
            doc?.FormatSizeName,
          ];
          return searchableFields.some((value) =>
            String(value ?? "").toLowerCase().includes(normalizedSearch)
          );
        })
      : baseDocuments;

    if (visibleDocuments.length > 0) {
      return getDocumentTableItems(
        visibleDocuments,
        workingDocumentList,
        recentCheckinDocForHighlight,
        docClassFormatList
      );
    }
    return [];
  }, [
    documentsForTable,
    workingDocumentList,
    recentCheckinDocForHighlight,
    lastExecutedSearchValue,
    isGeneralAppContext,
    docClassFormatList,
  ]);

  useEffect(() => {
    if (checkoutDocumentStatus === DOCUMENT_LOAD_STATUS.onUploadError) {
      performCheckout(checkoutDocumentForRefresh, tokenRefreshPreActionInvoke, true);
    }
  }, [checkoutDocumentStatus, checkoutDocumentForRefresh, performCheckout, tokenRefreshPreActionInvoke]);

  useEffect(() => {
    const justFinishedCheckoutOperation =
      prevCheckoutLoadingRef.current && !checkoutFlow?.loading;
    const isCheckoutSuccess =
      justFinishedCheckoutOperation &&
      !checkoutFlow?.loading &&
      checkoutFlow?.dialogVariant === "info" &&
      checkoutFlow?.lastOperationWasCheckout === true;
    const isUndoCheckoutSuccess =
      justFinishedCheckoutOperation &&
      !checkoutFlow?.loading &&
      checkoutFlow?.dialogVariant === "info" &&
      checkoutFlow?.lastOperationWasCheckout === false;
    if (isCheckoutSuccess && !hasNavigatedForCheckoutSuccess.current) {
      hasNavigatedForCheckoutSuccess.current = true;
      markAllDocumentsStaleAfterCheckout();
      navigate(APP_ROUTES.workingDocuments, {
        state: { [NAV_LOCATION_STATE.forceRefreshWorkingDocuments]: true },
      });
    }
    if (isUndoCheckoutSuccess && !hasRefreshedForUndoCheckoutSuccess.current) {
      hasRefreshedForUndoCheckoutSuccess.current = true;
      runSearch(selectedPageIndex);
      refreshWorkingDocumentsList();
    }
    // Reset guards when a new checkout starts so we navigate/refresh again on the next action.
    if (checkoutFlow?.loading) {
      hasNavigatedForCheckoutSuccess.current = false;
      hasRefreshedForUndoCheckoutSuccess.current = false;
    }
    prevCheckoutLoadingRef.current = Boolean(checkoutFlow?.loading);
  }, [
    checkoutFlow?.isDialogOpen,
    checkoutFlow?.loading,
    checkoutFlow?.dialogVariant,
    checkoutFlow?.lastOperationWasCheckout,
    navigate,
    runSearch,
    selectedPageIndex,
    requestTenantId,
    effectiveGroupId,
    effectiveChannelId,
    effectiveFileRoot,
    refreshWorkingDocumentsList,
    checkoutDocumentForRefresh,
    markAllDocumentsStaleAfterCheckout,
  ]);

  useEffect(() => {
    if (!isValidPlayload(tokenRefreshPreActionInvoke)) return;
    if (tokenRefreshPreActionInvoke.actionType === REFRESH_TOKEN_ACTION.documentCheckoutLoad) {
      performCheckout(
        checkoutDocumentForRefresh,
        tokenRefreshPreActionInvoke,
        false,
        checkoutUploadContext
      );
    } else if (tokenRefreshPreActionInvoke.actionType === REFRESH_TOKEN_ACTION.documentSearchLoad && requestTenantId) {
      runSearch(selectedPageIndex);
    }
  }, [tokenRefreshPreActionInvoke]);

  const checkIfDocumentIsDownloadedInOneDrive = (item) =>
    workingDocumentList?.some(
      (od) => od.name === generateWorkingDocumentFileName(item) && isWorkingDocumentDownloaded(od)
    ) ?? false;

  const handleDownloadClick = (item) => {
    setItemToDownload(item);
    if (checkIfDocumentIsDownloadedInOneDrive(item)) {
      setShowDownloadConfirm(true);
    } else {
      uploadDocumentToOneDrive(item, uploadContext ?? undefined);
    }
  };

  const handleDownloadConfirm = () => {
    if (itemToDownload) uploadDocumentToOneDrive(itemToDownload, uploadContext ?? undefined);
    setShowDownloadConfirm(false);
    setItemToDownload(null);
  };

  // Upload must follow live Teams scope, not cached requestGroupId/requestChannelId from a prior channel visit
  // (general + web have no teamsContext.channel.id → MainMenu + empty team/channel for API).
  const uploadContext = useMemo(
    () =>
      requestTenantId != null
        ? {
            tenantId: requestTenantId,
            teamId: effectiveGroupId ?? "",
            channelId: effectiveChannelId ?? "",
            oneDriveRoot: effectiveChannelId ? "ChannelDoc" : "MainMenu",
          }
        : null,
    [requestTenantId, effectiveGroupId, effectiveChannelId]
  );

  // For checkout only: avoid stale `requestChannelId` in general-app mode so the upload/placement happens in user's `/MainMenu` (AddoShare).
  const checkoutUploadContext = useMemo(
    () =>
      requestTenantId != null
        ? {
            tenantId: requestTenantId,
            teamId: effectiveGroupId ?? "",
            channelId: effectiveChannelId ?? "",
            membershipType: teamsContext?.channel?.membershipType ?? "Regular",
            // IMPORTANT: backend expects oneDriveRoot as a valid enum string.
            // If it's missing/undefined, it falls back to `Default` which uploads into a channel folder.
            oneDriveRoot: effectiveChannelId ? "ChannelDoc" : "MainMenu",
          }
        : null,
    [requestTenantId, effectiveGroupId, effectiveChannelId, teamsContext?.channel?.membershipType]
  );

  const documentTableActions = useMemo(
    () => ({
      onDetails: (item) => details.loadDocumentDetails(item),
      onCheckout: (item) => performCheckout(item, undefined, false, checkoutUploadContext),
      getCheckoutButtonText,
      onPreviewInTeams: (item) => previewInTeams(item, APP_ROUTES.allDocuments),
      onOpenIFS: openInIFS,
      onDownload: handleDownloadClick,
    }),
    [
      details,
      performCheckout,
      getCheckoutButtonText,
      previewInTeams,
      openInIFS,
      checkoutUploadContext,
      handleDownloadClick,
    ],
  );

  const columns = useDocumentTableColumns(documentTableActions);

  const hasCachedResults = lastSearchParams != null && data != null;
  const hasActiveAllDocsQuery =
    (lastExecutedSearchValue ?? "").trim().length > 0 ||
    (selectedFileType ?? "") !== "" ||
    !isUserInvolvedOnly;
  const showInitialBlockingLoader =
    searchLoading && !hasCachedResults && !hasActiveAllDocsQuery;

  return (
    <>
      {showInitialBlockingLoader ? (
        <Stack
          grow
          verticalAlign="center"
          horizontalAlign="center"
          styles={{ root: { minHeight: 0, height: "100%" } }}
        >
          <Loader />
        </Stack>
      ) : (
        <Stack
          enableScopedSelectors
          grow
          styles={{ root: { minHeight: 0, height: "100%" } }}
        >
          <Stack.Item shrink={0} styles={{ root: { flexShrink: 0 } }}>
            <AllDocumentsSearchBar
              searchValue={searchValue}
              onSearchValueChange={setSearchValue}
              onClearAndSearch={handleClearAndSearchAll}
              selectedFileType={selectedFileType}
              onFileTypeChange={setSelectedFileType}
              isFilterVisible={isFilterVisible}
              onFilterVisibleChange={setIsFilterVisible}
              isUserInvolvedOnly={isUserInvolvedOnly}
              onUserInvolvedOnlyChange={setIsUserInvolvedOnly}
              onSearch={handleSearchExecute}
              searchLoading={searchLoading}
              placeholder="Search for documents"
            />
          </Stack.Item>
          <Stack.Item
            grow
            styles={{
              root: {
                minHeight: 0,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              },
            }}
          >
            {searchLoading ? (
              <Stack
                horizontalAlign="center"
                verticalAlign="center"
                styles={{
                  root: {
                    width: "100%",
                    padding: "24px 0",
                    flexShrink: 0,
                    background: theme.colors.backgroundColor,
                  },
                }}
              >
                <Loader />
              </Stack>
            ) : (
              <Stack
                enableScopedSelectors
                grow
                styles={{
                  root: {
                    minHeight: 0,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  },
                }}
              >
                <Stack.Item
                  grow
                  className={classNames.wrapper}
                  styles={{
                    root: {
                      minHeight: 0,
                      overflow: "hidden",
                      flex: "1 1 auto",
                    },
                  }}
                >
                  <DocumentsTable
                    hidePagination
                    loading={false}
                    isError={searchError}
                    errorMessage={searchErrorMessage}
                    items={tableItems}
                    columns={columns}
                    selection={selection}
                  />
                </Stack.Item>
                <Stack.Item
                  shrink={0}
                  styles={{
                    root: {
                      flexShrink: 0,
                      background: theme.colors.backgroundColor,
                      padding: "6px 0 4px",
                    },
                  }}
                >
                  <DocumentsPagination
                    navigationLoading={searchLoading}
                    pageSize={pageSize}
                    documents={documentsForTable}
                    selectedPageIndex={selectedPageIndex}
                    onPageIndexChange={(idx) => goToPage(idx)}
                  />
                </Stack.Item>
              </Stack>
            )}
          </Stack.Item>
        </Stack>
      )}


      <ReusableDialog
        isOpen={previewState?.loading ?? false}
        onClose={() => { }}
        title="Preview in Teams"
        showHeader={false}
        variant="info"
        centerContent
        dialogMinHeight={40}
        bodyMinHeight={40}
      >
        <DocumentDetailsLoadingContent message={previewState?.loadingMessage} />
      </ReusableDialog>

      {/* Download Loading Dialog */}
      <ReusableDialog
        isOpen={uploadState?.loading ?? false}
        onClose={() => { }}
        title="Download"
        showHeader={false}
        variant="info"
        centerContent
        dialogMinHeight={40}
        bodyMinHeight={40}
      >
        <DocumentDetailsLoadingContent message="Downloading document.." />
      </ReusableDialog>

      {/* Overwrite Confirmation Dialog */}
      <ConfirmModal
        isOpen={showDownloadConfirm}
        onDismiss={() => {
          setShowDownloadConfirm(false);
          setItemToDownload(null);
        }}
        onConfirm={handleDownloadConfirm}
        onCancel={() => {
          setShowDownloadConfirm(false);
          setItemToDownload(null);
        }}
        title={OVERWRITE_DOCUMENT_CONFIRMATION_TITLE}
        subText={OVERWRITE_DOCUMENT_CONFIRMATION_MSG}
        confirmButtonText="Confirm"
        cancelButtonText="Cancel"
        theme={theme}
        type="confirm"
      />

      {/* Check-out / undo check-out: show only while in progress; success is toast-only (toastMiddleware). */}
      <ReusableMessageDialog
        isOpen={Boolean(checkoutFlow?.loading)}
        onClose={() => { }}
        title={checkoutFlow?.lastOperationWasCheckout ? "Check-out" : "Undo check-out"}
        showHeader={false}
        message={checkoutFlow?.message}
        variant={checkoutFlow?.dialogVariant ?? "info"}
        isLoading={checkoutFlow?.loading ?? false}
        loadingText={checkoutFlow?.message}
      />

      {/* IFS Dialog */}
      <ReusableMessageDialog
        isOpen={openIfsDialogOpen}
        onClose={closeOpenIfsDialog}
        title={openIfsDialogTitle}
        showHeader={false}
        message={openIfsDialogMessage}
        errorMessage={openIfsDialogDetails}
        variant={openIfsDialogType}
        showDetailsButton={!openIfsLoading}
        showOkButton={!openIfsLoading && openIfsDialogType !== "info"}
        isLoading={openIfsLoading}
        loadingText="Generating URLs..."
      />

    </>
  );
};

export default DocumentsContent;
